import type { WaterFallItem } from '@/app/(home-page)/types';
import type {
  WaterFall2Product,
  WaterFall2ResponseItem,
  WaterFall2RuntimeItem,
  WaterFall2StaticPosition,
} from './types';

export const WATERFALL2_PAGE_SIZE = 20;
export const WATERFALL2_PRODUCT_GROUP_SIZE = 5;
const WATERFALL2_SIMILAR_DISABLED_TAIL_SIZE = 2;
const CAMPAIGN_PATH_PATTERN = /\/campaigns\/([^/?#]+)/;

/** 标题行高固定为设计稿的 24px，切换布局或 Tab 时不改变标题区高度。 */
export const getWaterFall2TitlePresentation = (_columns: 1 | 2, configuredColor?: string) => ({
  className: 'font-bb font-bold leading-[24px]',
  color: configuredColor || '#282828',
});

const getPagePathname = (pageUrl: string) => {
  if (pageUrl.startsWith('//')) return new URL(`https:${pageUrl}`).pathname;
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(pageUrl)) return new URL(pageUrl).pathname;
  return pageUrl.split(/[?#]/, 1)[0];
};

export const getCampaignSlugFromPageUrl = (pageUrl?: string) => {
  if (!pageUrl) return '';

  try {
    const pathname = getPagePathname(pageUrl);
    const match = pathname.match(CAMPAIGN_PATH_PATTERN);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

const getProductKey = (product: WaterFall2Product, index: number) =>
  String(product.shop_sku || product.id || product.business_sku || `product-${index}`);

export const normalizeWaterFall2Product = (
  item: WaterFallItem | WaterFall2Product | Record<string, unknown>,
): WaterFall2Product => {
  const source = item as WaterFallItem & WaterFall2Product;
  const nestedProduct = source.product_info as unknown as WaterFall2Product | undefined;

  return {
    ...(nestedProduct || source),
    ...(nestedProduct?.image ? {} : source.image ? { image: source.image } : {}),
  } as WaterFall2Product;
};

export const hasCelebrityImage = (product: WaterFall2Product) =>
  Array.isArray(product.image?.celebrity) && product.image.celebrity.length > 0;

/**
 * 每 5 个商品形成一组。若组首没有红人图，则把组内第一个有红人图的商品前移，
 * 其余商品保持相对顺序；整组没有红人图时不调整。
 */
export const reorderProductsByCelebrity = (products: WaterFall2Product[]) => {
  const result: WaterFall2Product[] = [];

  for (let start = 0; start < products.length; start += WATERFALL2_PRODUCT_GROUP_SIZE) {
    const group = products.slice(start, start + WATERFALL2_PRODUCT_GROUP_SIZE);
    const celebrityIndex = group.findIndex(hasCelebrityImage);

    if (celebrityIndex > 0) {
      const [celebrityProduct] = group.splice(celebrityIndex, 1);
      group.unshift(celebrityProduct);
    }
    result.push(...group);
  }

  return result;
};

export const buildProductRuntimeItems = (
  products: WaterFall2Product[],
  applyCelebrityRule = true,
) =>
  (applyCelebrityRule ? reorderProductsByCelebrity(products) : products).map<WaterFall2RuntimeItem>(
    (product, index) => ({
      key: `product-${getProductKey(product, index)}`,
      kind: 'product',
      product,
      productIndex: index,
      isLarge: applyCelebrityRule && index % WATERFALL2_PRODUCT_GROUP_SIZE === 0,
    }),
  );

export const insertWaterFall2StaticPositions = (
  productItems: WaterFall2RuntimeItem[],
  staticPositions: WaterFall2StaticPosition[],
) => {
  const items = [...productItems];
  const orderedPositions = staticPositions
    .filter((item) => Number.isInteger(item.position) && item.position > 0)
    .sort((left, right) => left.position - right.position);

  orderedPositions.forEach((config) => {
    items.splice(Math.min(config.position - 1, items.length), 0, {
      key: `static-${config.position_type}-${config.id}`,
      kind: 'static',
      config,
    });
  });

  return items;
};

/**
 * 商品接口始终按 20 条稳定翻页；All Tab 的静态卡占用首批最终坑位。
 * 暂时隐藏等量尾部商品，加载下一页后它们会按原顺序进入可见区，避免改变接口 limit 导致跳 SKU。
 */
export const buildWaterFall2ProductAndStaticItems = (
  products: WaterFall2Product[],
  staticPositions: WaterFall2StaticPosition[],
) => {
  const validStaticPositions = staticPositions.filter(
    (item) => Number.isInteger(item.position) && item.position > 0,
  );
  const visibleProductCount = Math.max(products.length - validStaticPositions.length, 0);
  const productItems = buildProductRuntimeItems(products.slice(0, visibleProductCount));
  return insertWaterFall2StaticPositions(productItems, validStaticPositions);
};

export const isWaterFall2StaticPosition = (
  item: WaterFall2ResponseItem,
): item is WaterFall2StaticPosition => item.data_type === 'static_position';

/** 返回每列中下方仍有原始内容（商品卡或静态卡）的坑位 key。 */
export const getKeysWithFollowingColumnItem = (items: Array<{ key: string; column: number }>) => {
  const result = new Set<string>();
  const lastKeyByColumn = new Map<number, string>();

  items.forEach(({ key, column }) => {
    const previousKey = lastKeyByColumn.get(column);
    if (previousKey) result.add(previousKey);
    lastKeyByColumn.set(column, key);
  });

  return result;
};

/** 猜你喜欢要求锚点所在列有后续内容，且锚点不能是当前已加载商品的倒数 2 个。 */
export const canTriggerSimilarRecommendation = ({
  productIndex,
  productCount,
  hasFollowingColumnItem,
}: {
  productIndex: number;
  productCount: number;
  hasFollowingColumnItem: boolean;
}) => hasFollowingColumnItem && productIndex < productCount - WATERFALL2_SIMILAR_DISABLED_TAIL_SIZE;

export interface WaterFall2LayoutPosition {
  column: number;
  x: number;
  y: number;
}

/**
 * 原始内容按逻辑坑位从左到右落列，避免卡片高度变化打乱后台坑位。
 * 猜你喜欢固定落在锚点列；它之后的原始内容重新选择较短列，避免单列下推产生空洞。
 */
export const buildWaterFall2Layout = ({
  columns,
  items,
  itemHeights,
  itemWidth,
  itemGap,
  columnGap,
  defaultItemHeight = 240,
}: {
  columns: 1 | 2;
  items: WaterFall2RuntimeItem[];
  itemHeights: Record<string, number>;
  itemWidth: number;
  itemGap: number;
  columnGap: number;
  defaultItemHeight?: number;
}) => {
  const positions = new Map<string, WaterFall2LayoutPosition>();
  const columnHeights = new Array(columns).fill(0) as number[];
  const originalItemColumns: Array<{ key: string; column: number }> = [];
  let originalItemIndex = 0;
  let shouldReflowFollowingItems = false;

  items.forEach((item) => {
    const anchorPosition = item.kind === 'similar' ? positions.get(item.anchorKey) : undefined;
    if (item.kind === 'similar' && !anchorPosition) return;

    const column = anchorPosition
      ? anchorPosition.column
      : shouldReflowFollowingItems
        ? columnHeights.indexOf(Math.min(...columnHeights))
        : originalItemIndex % columns;
    const anchorBottom =
      item.kind === 'similar' && anchorPosition
        ? anchorPosition.y + (itemHeights[item.anchorKey] || defaultItemHeight) + itemGap
        : 0;
    const y = Math.max(columnHeights[column], anchorBottom);

    positions.set(item.key, {
      column,
      x: column * (itemWidth + columnGap),
      y,
    });
    columnHeights[column] = y + (itemHeights[item.key] || defaultItemHeight) + itemGap;

    if (item.kind === 'similar') {
      shouldReflowFollowingItems = true;
    } else {
      originalItemColumns.push({ key: item.key, column });
      originalItemIndex += 1;
    }
  });

  let height = 0;
  positions.forEach((position, key) => {
    height = Math.max(height, position.y + (itemHeights[key] || defaultItemHeight));
  });

  return { positions, height, originalItemColumns };
};
